import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GitBranch, Plus, AlertTriangle } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useFrameworks } from "../context/FrameworksContext";
import { iso27001Citation } from "../lib/standardCitation";
import { LoadingBlock } from "../components/Spinner";
import { PortalLink } from "../components/PortalLink";
import { PolicySaysChip } from "../components/PolicySaysChip";
import { NextSteps, type NextStepItem } from "../components/NextSteps";
import { DetailFields, DetailSection } from "../components/RecordView";
import { RegisterToolbar, SummaryTiles } from "../components/RegisterToolbar";
import { EntityTagsRow, EntityTagFilters, useEntityTagData, useTagFilter, matchesEntityTagFilter, entityRef } from "../components/EntityTags";
import { by, downloadCsv, matchesSearch, type SortOption } from "../lib/registerExport";
import {
  EmptyState, Field, ModalHeader, ModalOverlay, MultiPick, StatusPill, dangerBtn, fmtDateOnly, fmtDateTime, input, relativeDue,
  secondaryBtn, tableHeadRow, textarea, toDateInput, todayInput, nowLocalInput, fromInput, type AttentionItem,
} from "./resilience/shared";

// Change register — changes to systems, services and configuration (backend routes/changeRegister.routes.ts,
// lib/changeRegister.ts, docs/CHANGE-REGISTER.md). A change is approved on My Tasks by someone other than the
// requester, scheduled, made and reviewed; an emergency change may be made first and approved afterwards.
// Nothing deletes a change. Writing needs resilience:write; reading is open.

type ChangeStatus = "requested" | "approved" | "scheduled" | "implemented" | "reviewed" | "rejected" | "cancelled" | "failed";
type Move = "schedule" | "implement" | "review" | "cancel" | "fail";
interface Person { id: string; name: string | null }
interface ChangeRow {
  id: string; ref: string; title: string; description: string;
  changeType: "standard" | "normal" | "emergency"; changeTypeLabel: string; status: ChangeStatus; statusLabel: string;
  rollbackPlan: string | null; plannedDate: string | null;
  requestedBy: Person | null; approver: Person | null; selfApproved: boolean;
  decidedAt: string | null; approvedAt: string | null; approvedRetrospectively: boolean; decisionNote: string | null; awaitingDecision: boolean;
  implementedAt: string | null; implementedBy: Person | null;
  reviewOutcome: string | null; reviewOutcomeLabel: string | null; reviewNotes: string | null; reviewedAt: string | null; reviewedBy: Person | null;
  closedReason: string | null; closedAt: string | null;
  emergencyApprovalDue: string | null; emergencyReviewDue: string | null;
  moves: Move[];
  assets: Array<{ id: string; name: string; criticality: string; status: string }>;
  risks: Array<{ id: string; ref: string; title: string; status: string }>;
  createdAt: string;
}
interface Params { emergencyApprovalDays: number; emergencyReviewDays: number; incidentLookbackDays: number }
interface View {
  changes: ChangeRow[]; params: Params; attention: AttentionItem[];
  members: Array<{ id: string; name: string }>;
  assets: Array<{ id: string; name: string }>;
  risks: Array<{ id: string; name: string }>;
  me: string;
}

const STATUS_PILL: Record<ChangeStatus, string> = {
  requested: "pending", approved: "approved", scheduled: "planned", implemented: "in_progress",
  reviewed: "completed", rejected: "cancelled", cancelled: "cancelled", failed: "overdue",
};
const TYPE_HELP: Record<ChangeRow["changeType"], string> = {
  standard: "Pre-approved and repeatable, with a known way back (a routine patch, a new starter's laptop).",
  normal: "Assessed and approved before it is made.",
  emergency: "Made at once to stop or fix harm; approved and reviewed afterwards.",
};
const OUTCOMES = [
  { id: "successful", label: "Successful" },
  { id: "partially_successful", label: "Partly successful" },
  { id: "unsuccessful", label: "Unsuccessful" },
];
const SEV_COLOR: Record<AttentionItem["severity"], string> = { high: "var(--c-danger)", medium: "var(--c-warning)", low: "var(--c-borderStrong)" };
const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, verticalAlign: "top" };
const errorText: React.CSSProperties = { ...text.hint, color: theme.color.dangerText, marginTop: 10 };
const OPEN: readonly ChangeStatus[] = ["requested", "approved", "scheduled", "implemented", "failed"];
const isOpen = (c: ChangeRow) => OPEN.includes(c.status) && !(c.status === "failed" && c.reviewedAt);

type TileFilter = "open" | "decision" | "emergency" | "all";

const SORTS: SortOption<ChangeRow>[] = [
  { key: "ref", label: "Newest first", compare: by.date((c) => c.createdAt, "desc") },
  { key: "planned", label: "Planned date", compare: by.date((c) => c.plannedDate) },
  { key: "made", label: "Most recently made", compare: by.date((c) => c.implementedAt, "desc") },
  { key: "title", label: "Title", compare: by.text((c) => c.title) },
];

export function ChangeRegisterPage() {
  const { enrolled } = useFrameworks();
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("resilience:write");
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<View | null>(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<{ change: ChangeRow | null } | null>(null);
  const [filter, setFilter] = useState<TileFilter>("open");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("ref");
  const tagData = useEntityTagData();
  const [tagFilter, setTagFilter] = useTagFilter();

  const load = useCallback(async () => {
    try { setData(await api.get<View>("/changes")); setError(""); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the change register.")); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const openId = searchParams.get("change");
  const setOpen = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("change", id); else next.delete("change");
    setSearchParams(next, { replace: true });
  };

  const all = data?.changes ?? [];
  const emergencyOpen = (c: ChangeRow) => c.changeType === "emergency" && (c.status === "implemented" || c.status === "failed") && !c.reviewedAt;
  const rows = useMemo(() => {
    const keep = (c: ChangeRow) =>
      (filter === "all" || (filter === "open" && isOpen(c)) || (filter === "decision" && c.awaitingDecision) || (filter === "emergency" && emergencyOpen(c)))
      && matchesSearch(search, c.ref, c.title, c.description, c.requestedBy?.name, c.approver?.name, ...c.assets.map((a) => a.name))
      && matchesEntityTagFilter(tagData, entityRef({ kind: "change", id: c.id }), tagFilter);
    const cmp = SORTS.find((s) => s.key === sort)?.compare ?? SORTS[0].compare;
    return all.filter(keep).sort(cmp);
  }, [all, filter, search, sort, tagData, tagFilter]);
  const open = all.find((c) => c.id === openId) ?? null;
  const yearAgo = Date.now() - 365 * 86_400_000;

  const exportCsv = () => downloadCsv("change-register", [
    { header: "Ref", value: (c: ChangeRow) => c.ref },
    { header: "Title", value: (c) => c.title },
    { header: "Type", value: (c) => c.changeTypeLabel },
    { header: "Status", value: (c) => c.statusLabel },
    { header: "Assets", value: (c) => c.assets.map((a) => a.name).join("; ") },
    { header: "Requested by", value: (c) => c.requestedBy?.name },
    { header: "Approver", value: (c) => c.approver?.name },
    { header: "Decided", value: (c) => c.decidedAt?.slice(0, 10) },
    { header: "Planned", value: (c) => c.plannedDate?.slice(0, 10) },
    { header: "Made", value: (c) => c.implementedAt },
    { header: "Review outcome", value: (c) => c.reviewOutcomeLabel },
  ], rows);

  return (
    <div className="page-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
            <GitBranch size={20} /> Change register
          </h1>
          <p style={{ ...pageSubtitle, maxWidth: 780 }}>
            Changes to your systems, services and configuration{iso27001Citation(enrolled, "A.8.32")}: what changes, the assets it touches, how it is
            rolled back, who approved it and how it went. Someone other than the requester approves it on My Tasks. An emergency change may be made
            first and approved afterwards, then reviewed.
          </p>
          <PolicySaysChip keys={["change.emergency_approval_days", "change.emergency_review_days", "change.incident_lookback_days"]} />
        </div>
        {canEdit && data && (
          <button type="button" style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }} onClick={() => setEditing({ change: null })}>
            <Plus size={14} /> Request a change
          </button>
        )}
      </div>

      {error && <div style={{ ...errorText, marginTop: 0, marginBottom: 12 }}>{error}</div>}

      {!data ? (error ? null : <LoadingBlock />) : (
        <div style={{ display: "grid", gap: 20 }}>
          <SummaryTiles tiles={[
            { key: "open", label: "Open", value: all.filter(isOpen).length, hint: "requested, approved, scheduled or awaiting review", onClick: () => setFilter("open"), active: filter === "open" },
            { key: "decision", label: "Awaiting a decision", value: all.filter((c) => c.awaitingDecision).length, tone: all.some((c) => c.awaitingDecision) ? "accent" : "neutral", onClick: () => setFilter("decision"), active: filter === "decision" },
            { key: "emergency", label: "Emergency changes to close", value: all.filter(emergencyOpen).length, hint: `approve within ${data.params.emergencyApprovalDays} days, review within ${data.params.emergencyReviewDays}`, tone: all.some(emergencyOpen) ? "warning" : "neutral", onClick: () => setFilter("emergency"), active: filter === "emergency" },
            { key: "all", label: "Made in 12 months", value: all.filter((c) => c.implementedAt && new Date(c.implementedAt).getTime() >= yearAgo).length, hint: "show every change", onClick: () => setFilter("all"), active: filter === "all" },
          ]} />

          {data.attention.length > 0 && (
            <div style={{ ...card, padding: 0 }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8, ...text.sectionTitle, margin: 0 }}>
                <AlertTriangle size={15} /> Needs attention
                <span style={{ ...text.hint, fontWeight: 400 }}>{data.attention.length}</span>
              </div>
              {data.attention.map((a) => (
                <PortalLink key={a.id} to={a.href} style={{ textDecoration: "none" }}>
                  <div style={{ display: "flex", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "flex-start" }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, marginTop: 6, background: SEV_COLOR[a.severity], flexShrink: 0 }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={text.itemTitle}>{a.title}</div>
                      <div style={{ ...text.hint, marginTop: 2 }}>{a.description}</div>
                    </div>
                    {a.dueAt && <span style={{ fontSize: text.meta.fontSize, color: a.severity === "high" ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
                  </div>
                </PortalLink>
              ))}
            </div>
          )}

          <div style={{ ...card, padding: 0, overflowX: "auto" }}>
            <div style={{ padding: "12px 18px 0" }}>
              <RegisterToolbar search={search} onSearch={setSearch} placeholder="Search changes" sortOptions={SORTS} sort={sort} onSort={setSort}
                onExport={rows.length ? exportCsv : undefined} count={{ shown: rows.length, total: all.length }}>
                <EntityTagFilters value={tagFilter} onChange={setTagFilter} />
              </RegisterToolbar>
            </div>
            {rows.length === 0 ? (
              <div style={{ padding: 18 }}>
                <EmptyState
                  title={all.length ? "No changes match" : "No changes recorded yet"}
                  detail="Record a change before it is made, name the assets it touches and how it is rolled back, and have someone else approve it. Record an emergency change as soon as it is made."
                />
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                <thead><tr style={tableHeadRow}>
                  <th style={th}>Ref</th><th style={th}>Change</th><th style={th}>Type</th><th style={th}>Status</th><th style={th}>Planned</th><th style={th}>Made</th><th style={th}>Approver</th>
                </tr></thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id} onClick={() => setOpen(c.id)} style={{ cursor: "pointer", borderBottom: "1px solid var(--c-border)" }}>
                      <td style={{ ...td, ...text.ref, color: theme.color.text, fontWeight: 600 }}>{c.ref}</td>
                      <td style={{ ...td, color: theme.color.text }}>{c.title}{c.assets.length > 0 && <div style={text.hint}>{c.assets.map((a) => a.name).join(", ")}</div>}</td>
                      <td style={{ ...td, color: c.changeType === "emergency" ? theme.color.warningText : td.color }}>{c.changeTypeLabel}</td>
                      <td style={td}><StatusPill value={STATUS_PILL[c.status]} label={c.awaitingDecision && c.status !== "requested" ? `${c.statusLabel}, awaiting approval` : c.statusLabel} /></td>
                      <td style={td}>{c.plannedDate ? fmtDateOnly(c.plannedDate) : "-"}</td>
                      <td style={td}>{c.implementedAt ? fmtDateTime(c.implementedAt) : "-"}</td>
                      <td style={td}>{c.approver?.name ?? "-"}{c.selfApproved ? " (sole member)" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div style={text.hint}>Every change is kept, whatever happens to it. Incidents list the recent changes made to the assets they touched.</div>
        </div>
      )}

      {editing && data && (
        <ChangeForm view={data} change={editing.change} onClose={() => setEditing(null)}
          onSaved={(id) => { setEditing(null); void load(); setOpen(id); }} />
      )}
      {open && data && !editing && (
        <ChangeRecord change={open} view={data} canEdit={canEdit} onClose={() => setOpen(null)} onChanged={load}
          onEdit={() => setEditing({ change: open })} />
      )}
    </div>
  );
}

function ChangeForm({ view, change, onClose, onSaved }: { view: View; change: ChangeRow | null; onClose: () => void; onSaved: (id: string) => void }) {
  const [title, setTitle] = useState(change?.title ?? "");
  const [description, setDescription] = useState(change?.description ?? "");
  const [changeType, setType] = useState<ChangeRow["changeType"]>(change?.changeType ?? "normal");
  const [rollbackPlan, setRollback] = useState(change?.rollbackPlan ?? "");
  const [plannedDate, setPlanned] = useState(change?.plannedDate ? toDateInput(change.plannedDate) : "");
  const [approverUserId, setApprover] = useState(change?.approver?.id ?? "");
  const [assetIds, setAssets] = useState<string[]>(change?.assets.map((a) => a.id) ?? []);
  const [riskIds, setRisks] = useState<string[]>(change?.risks.map((r) => r.id) ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const requesterId = change?.requestedBy?.id ?? view.me;
  const approvers = view.members.filter((m) => m.id !== requesterId || view.members.length === 1);
  const madeAlready = !!change && change.status !== "requested";
  const plannedChanged = plannedDate !== (change?.plannedDate ? toDateInput(change.plannedDate) : "");
  const steps: NextStepItem[] = [
    { done: title.trim().length >= 3, label: "A short title" },
    { done: description.trim().length >= 10, label: "What changes and why" },
    ...(changeType !== "standard" ? [{ done: rollbackPlan.trim().length >= 10, label: "How it is rolled back if it goes wrong" }] : []),
    { done: !!approverUserId, label: "An approver other than the requester" },
    { done: assetIds.length > 0, label: "The assets it touches", hint: "Optional, but an incident on those assets can only show this change if they are named." },
    ...(changeType !== "emergency" && plannedDate && plannedChanged ? [{ done: plannedDate >= todayInput(), label: "A planned date from today", urgent: plannedDate < todayInput() }] : []),
  ];
  const required = steps.filter((s) => s.label !== "The assets it touches");

  const submit = async () => {
    setBusy(true); setError("");
    const body = {
      title, description, changeType, rollbackPlan: rollbackPlan.trim() || null, plannedDate: plannedDate || null,
      approverUserId, assetIds, riskIds,
    };
    try {
      if (change) {
        await api.patch(`/changes/${change.id}`, madeAlready ? { ...body, changeType: undefined } : body);
        onSaved(change.id);
      } else {
        const res = await api.post<{ change: ChangeRow }>("/changes", body);
        onSaved(res.change.id);
      }
    } catch (e) { setError(apiErrorMessage(e, "The change could not be saved.")); }
    finally { setBusy(false); }
  };

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={change ? `Edit ${change.ref}` : "Request a change"} onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 14 }}>
        <NextSteps title="Before you send it" items={steps} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
          <Field label="Title" span2><input style={input} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label="Type" help={TYPE_HELP[changeType]}>
            <select style={input} value={changeType} disabled={madeAlready} onChange={(e) => setType(e.target.value as ChangeRow["changeType"])}>
              <option value="standard">Standard</option>
              <option value="normal">Normal</option>
              <option value="emergency">Emergency</option>
            </select>
          </Field>
          <Field label="Planned date" help={changeType === "emergency" ? "Leave empty for a change already made; record when on the change." : "The day it is to be made. Scheduling it later puts it on the calendar."}>
            <input type="date" style={input} value={plannedDate} min={todayInput()} onChange={(e) => setPlanned(e.target.value)} />
          </Field>
          <Field label="What changes and why" span2><textarea style={textarea} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
          <Field label="Rollback plan" span2 help={changeType === "standard" ? "Optional for a standard change." : "How the change is undone if it goes wrong, and who decides."}>
            <textarea style={textarea} rows={2} value={rollbackPlan} onChange={(e) => setRollback(e.target.value)} />
          </Field>
          <Field label="Approver" help="Someone other than the requester. They decide on My Tasks.">
            <select style={input} value={approverUserId} onChange={(e) => setApprover(e.target.value)}>
              <option value="">Choose a member</option>
              {approvers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
        </div>
        <DetailSection title="What it touches">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
            <Field label="Assets"><MultiPick options={view.assets} selected={assetIds} onChange={setAssets} empty="No assets in the register." /></Field>
            <Field label="Risks it bears on"><MultiPick options={view.risks} selected={riskIds} onChange={setRisks} empty="No open risks." /></Field>
          </div>
        </DetailSection>
        {error && <div style={errorText}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="button" style={btnPrimary} disabled={busy || required.some((s) => !s.done)} onClick={() => void submit()}>
            {busy ? "Saving..." : change ? "Save" : "Send for approval"}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function ChangeRecord({ change: c, view, canEdit, onClose, onChanged, onEdit }: {
  change: ChangeRow; view: View; canEdit: boolean; onClose: () => void; onChanged: () => void; onEdit: () => void;
}) {
  const [acting, setActing] = useState<Move | null>(null);
  const [plannedDate, setPlanned] = useState(c.plannedDate && toDateInput(c.plannedDate) >= todayInput() ? toDateInput(c.plannedDate) : todayInput());
  const [implementedAt, setImplementedAt] = useState(nowLocalInput());
  const [outcome, setOutcome] = useState("successful");
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (move: Move) => {
    setError(""); setBusy(true);
    const body = move === "schedule" ? { plannedDate }
      : move === "implement" ? { implementedAt: fromInput(implementedAt) }
      : move === "review" ? { reviewOutcome: outcome, reviewNotes: notes }
      : { reason, ...(move === "fail" && !c.implementedAt ? { implementedAt: fromInput(implementedAt) } : {}) };
    try { await api.post(`/changes/${c.id}/${move}`, body); setActing(null); setReason(""); setNotes(""); onChanged(); }
    catch (e) { setError(apiErrorMessage(e, "The change could not be updated.")); }
    finally { setBusy(false); }
  };
  const reassign = async (field: "requestedByUserId" | "approverUserId", id: string) => {
    setError("");
    try { await api.patch(`/changes/${c.id}/people`, { [field]: id }); onChanged(); }
    catch (e) { setError(apiErrorMessage(e, "The change could not be reassigned.")); }
  };

  const final = c.status === "reviewed" || c.status === "rejected" || c.status === "cancelled";
  const steps: NextStepItem[] = final ? [] : [
    ...(c.awaitingDecision ? [{
      done: false, label: `${c.approver?.name ?? "The approver"} decides it on My Tasks${c.status !== "requested" ? " (after the fact)" : ""}`,
      hint: c.emergencyApprovalDue && c.status !== "requested" ? `Due by ${fmtDateOnly(c.emergencyApprovalDue)}.` : undefined,
      urgent: !!c.emergencyApprovalDue && c.status !== "requested" && new Date(c.emergencyApprovalDue).getTime() < Date.now(),
    }] : []),
    ...(c.changeType !== "standard" ? [{ done: !!c.rollbackPlan, label: "A rollback plan" }] : []),
    { done: c.assets.length > 0, label: "The assets it touches" },
    ...(c.status === "approved" || c.status === "scheduled" ? [{
      done: false, label: c.status === "approved" ? "Schedule it, or record when it was made" : `Record when it was made${c.plannedDate ? ` (planned for ${fmtDateOnly(c.plannedDate)})` : ""}`,
      urgent: !!c.plannedDate && toDateInput(c.plannedDate) < todayInput(),
    }] : []),
    ...(c.status === "implemented" || c.status === "failed" ? [{
      done: false, label: "Hold the post-implementation review",
      hint: c.emergencyReviewDue ? `Due by ${fmtDateOnly(c.emergencyReviewDue)}.` : "What it achieved, side effects, and what records were updated.",
      urgent: !!c.emergencyReviewDue && new Date(c.emergencyReviewDue).getTime() < Date.now(),
    }] : []),
  ];

  const MOVE_LABEL: Record<Move, string> = { schedule: "Schedule", implement: "Record as made", review: "Record the review", cancel: "Cancel", fail: "Mark failed" };

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={<span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}><span style={{ ...text.ref, fontSize: "inherit", color: theme.color.muted }}>{c.ref}</span>{c.title}</span>} onClose={onClose}
        right={<StatusPill value={STATUS_PILL[c.status]} label={c.statusLabel} />} />
      <div style={{ padding: 20, display: "grid", gap: 4 }}>
        <NextSteps items={steps} action={canEdit && c.awaitingDecision ? <button type="button" style={secondaryBtn} onClick={onEdit}>Fill these in</button> : undefined} />
        <DetailSection title="The change">
          <div>
            <DetailFields fields={[
              { label: "Type", value: c.changeTypeLabel },
              { label: "Planned", value: c.plannedDate ? fmtDateOnly(c.plannedDate) : null },
              { label: "What changes and why", value: c.description, wide: true, multiline: true },
              { label: "Rollback plan", value: c.rollbackPlan, wide: true, multiline: true },
              { label: "Assets", value: c.assets.length ? c.assets.map((a) => <PortalLink key={a.id} to={`/resilience/assets?asset=${a.id}`} style={{ color: "var(--c-accentText)", marginRight: 10 }}>{a.name}</PortalLink>) : null, wide: true },
              { label: "Risks", value: c.risks.length ? c.risks.map((r) => <PortalLink key={r.id} to={`/risk-register?risk=${r.id}`} style={{ color: "var(--c-accentText)", marginRight: 10 }}>{r.ref} {r.title}</PortalLink>) : null, wide: true },
            ]} />
          </div>
        </DetailSection>
        <DetailSection title="Approval">
          <div>
            <DetailFields fields={[
              { label: "Requested by", value: c.requestedBy?.name },
              { label: "Approver", value: `${c.approver?.name ?? "-"}${c.selfApproved ? " (sole member)" : ""}` },
              { label: "Decision", value: c.decidedAt ? `${c.approvedAt ? (c.approvedRetrospectively ? "Approved after the fact" : "Approved") : "Refused"} ${fmtDateTime(c.decidedAt)}${c.decisionNote ? `: ${c.decisionNote}` : ""}` : "Not decided yet" },
            ]} />
          </div>
        </DetailSection>
        {(c.implementedAt || c.reviewedAt || c.closedReason) && (
          <DetailSection title="Made and reviewed">
            <div>
              <DetailFields fields={[
                { label: "Made", value: c.implementedAt ? `${fmtDateTime(c.implementedAt)}${c.implementedBy?.name ? `, recorded by ${c.implementedBy.name}` : ""}` : null },
                { label: c.status === "cancelled" ? "Cancelled" : c.status === "rejected" ? "Rejected" : "What went wrong", value: c.closedReason ? `${c.closedAt ? `${fmtDateOnly(c.closedAt)}: ` : ""}${c.closedReason}` : null, wide: true, multiline: true },
                { label: "Review", value: c.reviewedAt ? `${c.reviewOutcomeLabel} (${fmtDateOnly(c.reviewedAt)}${c.reviewedBy?.name ? `, ${c.reviewedBy.name}` : ""})` : null },
                { label: "What the review found", value: c.reviewNotes, wide: true, multiline: true },
              ]} />
            </div>
          </DetailSection>
        )}

        <div style={{ marginTop: 8 }}>
          <EntityTagsRow entity={{ kind: "change", id: c.id }} readOnly={!canEdit} />
        </div>

        {error && <div style={errorText}>{error}</div>}
        {canEdit && !final && (
          <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
            {acting ? (
              <div style={{ display: "grid", gap: 8 }}>
                {acting === "schedule" && <Field label="Planned date"><input type="date" style={{ ...input, maxWidth: 220 }} value={plannedDate} min={todayInput()} onChange={(e) => setPlanned(e.target.value)} /></Field>}
                {(acting === "implement" || (acting === "fail" && !c.implementedAt)) && (
                  <Field label={acting === "implement" ? "When was it made?" : "When was it attempted?"}>
                    <input type="datetime-local" style={{ ...input, maxWidth: 260 }} value={implementedAt} max={nowLocalInput()} onChange={(e) => setImplementedAt(e.target.value)} />
                  </Field>
                )}
                {acting === "review" && (
                  <>
                    <Field label="Did it achieve what it set out to?">
                      <select style={{ ...input, maxWidth: 260 }} value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                        {OUTCOMES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                    </Field>
                    <Field label="What the review found" help="Side effects, whether it was rolled back, and what was updated: asset register, baselines, diagrams, procedures.">
                      <textarea style={textarea} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </Field>
                  </>
                )}
                {(acting === "cancel" || acting === "fail") && (
                  <Field label={acting === "cancel" ? "Why is it cancelled?" : "What went wrong, and was it rolled back?"}>
                    <textarea style={textarea} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" style={secondaryBtn} onClick={() => setActing(null)}>Back</button>
                  <button type="button" style={acting === "cancel" || acting === "fail" ? dangerBtn : btnPrimary} disabled={busy} onClick={() => void act(acting)}>
                    {busy ? "Saving..." : MOVE_LABEL[acting]}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                {c.moves.includes("cancel") && <button type="button" style={dangerBtn} onClick={() => setActing("cancel")}>Cancel change</button>}
                {c.moves.includes("fail") && <button type="button" style={dangerBtn} onClick={() => setActing("fail")}>Mark failed</button>}
                {c.awaitingDecision && <button type="button" style={secondaryBtn} onClick={onEdit}>Edit</button>}
                {c.moves.includes("schedule") && <button type="button" style={secondaryBtn} onClick={() => setActing("schedule")}>Schedule</button>}
                {c.moves.includes("implement") && <button type="button" style={btnPrimary} onClick={() => setActing("implement")}>{c.status === "requested" ? "Record as made (emergency)" : "Record as made"}</button>}
                {c.moves.includes("review") && <button type="button" style={btnPrimary} disabled={!c.decidedAt} title={c.decidedAt ? undefined : "The approver decides it first"} onClick={() => setActing("review")}>Record the review</button>}
              </div>
            )}
            <details>
              <summary style={{ ...text.hint, cursor: "pointer" }}>Hand it to someone else</summary>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8 }}>
                <Field label="Requester">
                  <select style={{ ...input, minWidth: 220 }} value={c.requestedBy?.id ?? ""} onChange={(e) => void reassign("requestedByUserId", e.target.value)}>
                    {!view.members.some((m) => m.id === c.requestedBy?.id) && <option value={c.requestedBy?.id ?? ""}>{c.requestedBy?.name ?? "Nobody"} (no longer active)</option>}
                    {view.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </Field>
                {c.awaitingDecision && (
                  <Field label="Approver">
                    <select style={{ ...input, minWidth: 220 }} value={c.approver?.id ?? ""} onChange={(e) => void reassign("approverUserId", e.target.value)}>
                      {!view.members.some((m) => m.id === c.approver?.id) && <option value={c.approver?.id ?? ""}>{c.approver?.name ?? "Nobody"} (no longer active)</option>}
                      {view.members.filter((m) => m.id !== c.requestedBy?.id || view.members.length === 1).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </Field>
                )}
              </div>
            </details>
          </div>
        )}
        <div style={{ ...text.hint, marginTop: 8 }}>A change is kept whatever happens to it; nothing deletes one.</div>
      </div>
    </ModalOverlay>
  );
}
